import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';

/** Cached promise so the CSRF token endpoint is only hit once per page load. */
let csrfTokenPromise = null;

/**
 * Fetches and caches the AEM Granite CSRF token.
 * Subsequent calls return the same promise without making a new network request.
 * On failure the cache is cleared so the next call can retry.
 * @returns {Promise<string>}
 */
async function fetchCsrfToken() {
  if (!csrfTokenPromise) {
    csrfTokenPromise = fetch(`${globalThis?.location?.origin}/libs/granite/csrf/token.json`)
      .then((res) => {
        if (!res.ok) throw new Error(`CSRF fetch failed: ${res.status}`);
        return res.json();
      })
      .then((data) => data.token)
      .catch((err) => {
        csrfTokenPromise = null;
        throw err;
      });
  }
  return csrfTokenPromise;
}

/**
 * Base API for AEM same-origin endpoints (e.g. /bin/esri/*).
 *
 * Differences from base-api.js (eCommUrl):
 * - baseUrl is always the current page origin, not window.storeVariables.eCommUrl
 * - CSRF-Token is automatically fetched and cached for all mutating requests
 *
 * Inject endpoints into this API when targeting AEM servlets on the same domain.
 */
export const baseAemApi = createApi({
  reducerPath: 'aemApi',
  baseQuery: fetchBaseQuery({
    baseUrl: globalThis?.location?.origin,
    prepareHeaders: async (headers) => {
      try {
        const csrfToken = await fetchCsrfToken();
        headers.set('CSRF-Token', csrfToken);
      } catch {
        // Proceed without CSRF token; the servlet may still allow the request
      }

      return headers;
    },
  }),
  tagTypes: [],
  endpoints: () => ({}),
});
